import { useState, useEffect, useRef, useCallback } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useAlerts } from "../context/AlertContext";
import { Button } from "../components/ui/button";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Activity,
  LayoutDashboard,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Bell,
  Pickaxe,
  Wifi,
  WifiOff,
  Database,
  Swords,
  Radio,
  ShieldCheck,
  Server,
} from "lucide-react";
import axios from "axios";

import { API } from '../config';

const navItems = [
  { path: "/eu-bot", label: "EU Bot", icon: Server },
  { path: "/settings", label: "Settings", icon: Settings },
];

/* ============ Animated number component ============ */
const LiveNumber = ({ value, prefix = "", suffix = "", decimals = 2, className = "" }) => {
  const [display, setDisplay] = useState(value);
  const [flash, setFlash] = useState("");
  const prev = useRef(value);

  useEffect(() => {
    if (value !== prev.current) {
      setFlash(value > prev.current ? "flash-green" : "flash-red");
      prev.current = value;
      const timer = setTimeout(() => setFlash(""), 600);
      return () => clearTimeout(timer);
    }
    setDisplay(value);
  }, [value]);

  useEffect(() => {
    setDisplay(value);
  }, [value]);

  const formatted = typeof display === "number" && !isNaN(display)
    ? display.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })
    : "---";

  return (
    <span className={`font-mono transition-colors duration-300 ${flash === "flash-green" ? "text-success" : flash === "flash-red" ? "text-destructive" : ""} ${className}`}>
      {prefix}{formatted}{suffix}
    </span>
  );
};

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const { unreadCount, wsConnected, alerts } = useAlerts();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [riskStatus, setRiskStatus] = useState(null);
  const [bellOpen, setBellOpen] = useState(false);
  const [sessionStats, setSessionStats] = useState(null);
  const [livePrices, setLivePrices] = useState({});
  const [sparklines, setSparklines] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [riskRes, sessRes, priceRes] = await Promise.all([
        axios.get(`${API}/risk/status`).catch(() => null),
        axios.get(`${API}/analytics/session`).catch(() => null),
        axios.get(`${API}/prices/live`).catch(() => null),
      ]);
      if (riskRes) setRiskStatus(riskRes.data);
      if (sessRes) setSessionStats(sessRes.data);
      if (priceRes) setLivePrices(priceRes.data);
    } catch {}
  }, []);

  // Fetch sparklines less frequently (every 60s)
  const fetchSparklines = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/prices/sparklines`);
      setSparklines(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchData();
    fetchSparklines();
    const interval = setInterval(fetchData, 15000);
    const sparkInterval = setInterval(fetchSparklines, 60000);
    return () => { clearInterval(interval); clearInterval(sparkInterval); };
  }, [fetchData, fetchSparklines]);

  return (
    <div className="min-h-screen bg-background flex">
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-56 bg-card border-r border-border transform transition-transform duration-200 ease-in-out ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="flex flex-col h-full">
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary flex items-center justify-center">
                  <Activity className="w-5 h-5 text-black" />
                </div>
                <span className="font-heading font-bold text-lg">SKYNET</span>
              </div>
              <button className="lg:hidden text-muted-foreground hover:text-foreground" onClick={() => setSidebarOpen(false)}>
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <ScrollArea className="flex-1 py-3">
            <nav className="px-2 space-y-0.5">
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.path === "/"}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-2 text-sm transition-colors ${
                      isActive
                        ? "bg-primary/10 text-primary border-l-2 border-primary"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    }`
                  }
                  data-testid={`nav-${item.path.replace("/", "") || "dashboard"}`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </ScrollArea>

          {/* Account */}
          <div className="p-3 border-t border-border space-y-3">
            {riskStatus && (
              <div className="space-y-2">
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Risk Level</span>
                  <span className={riskStatus.portfolio_heat > riskStatus.portfolio_heat_cap * 0.8 ? "text-destructive" : "text-success"}>
                    {riskStatus.portfolio_heat.toFixed(1)}%
                  </span>
                </div>
                <div className="h-1 bg-muted overflow-hidden">
                  <div
                    className={`h-full transition-all ${riskStatus.portfolio_heat > riskStatus.portfolio_heat_cap * 0.8 ? "bg-destructive" : "bg-success"}`}
                    style={{ width: `${Math.min(100, (riskStatus.portfolio_heat / riskStatus.portfolio_heat_cap) * 100)}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs">
                  <span className="text-muted-foreground">Today</span>
                  <span className={riskStatus.daily_pnl >= 0 ? "text-success font-mono" : "text-destructive font-mono"}>
                    {riskStatus.daily_pnl >= 0 ? "+" : ""}{riskStatus.daily_pnl_pct.toFixed(2)}%
                  </span>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between">
              <div className="text-xs">
                <div className="text-muted-foreground">{user?.username}</div>
                <div className="text-primary font-mono font-bold">
                  <LiveNumber value={riskStatus?.equity || 100000} prefix="$" decimals={0} />
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={logout} className="text-muted-foreground hover:text-destructive" data-testid="logout-btn">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 flex flex-col min-h-screen">
        {/* === PERSISTENT PRICE TICKER BAR === */}
        <div className="h-8 bg-card/80 border-b border-border/50 flex items-center justify-between px-4 overflow-hidden" data-testid="price-ticker-bar">
          <div className="flex items-center gap-4 text-xs font-mono overflow-x-auto no-scrollbar">
            <PricePill label="BTC" value={livePrices.BTCUSDT} sparkline={sparklines.BTCUSDT} />
            <PricePill label="ETH" value={livePrices.ETHUSDT} sparkline={sparklines.ETHUSDT} />
            <PricePill label="SOL" value={livePrices.SOLUSDT} sparkline={sparklines.SOLUSDT} />
            <PricePill label="GOLD" value={livePrices.PAXGUSDT} sparkline={sparklines.PAXGUSDT} />
            <PricePill label="BNB" value={livePrices.BNBUSDT} sparkline={sparklines.BNBUSDT} />
            <PricePill label="SPX" value={livePrices.SPX500} decimals={0} />
            {livePrices.agent_running && (
              <div className="flex items-center gap-1.5 text-primary" data-testid="price-agent">
                <span className="text-[10px] text-primary/70">BOT</span>
                <span className={`font-semibold ${livePrices.agent_pnl >= 0 ? "text-success" : "text-destructive"}`}>
                  {livePrices.agent_pnl >= 0 ? "+" : ""}${(livePrices.agent_pnl || 0).toFixed(2)}
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
            <div className="px-1.5 py-0.5 text-[10px] font-mono text-primary bg-primary/10">
              DIAMOND
            </div>
          </div>
        </div>

        {/* Top bar */}
        <header className="h-12 border-b border-border flex items-center justify-between px-4 lg:px-6 glass sticky top-0 z-30">
          <div className="flex items-center gap-4">
            <button className="lg:hidden text-muted-foreground hover:text-foreground" onClick={() => setSidebarOpen(true)} data-testid="mobile-menu-btn">
              <Menu className="w-5 h-5" />
            </button>
            <div className="flex items-center text-sm text-muted-foreground">
              <span className="hidden sm:inline">SKYNET</span>
              <ChevronRight className="w-4 h-4 mx-1" />
              <span className="text-foreground font-medium">
                {navItems.find((item) => (item.path === "/" ? location.pathname === "/" : location.pathname.startsWith(item.path)))?.label || "Dashboard"}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Connection */}
            <div className="hidden sm:flex items-center gap-1 text-xs text-muted-foreground" data-testid="ws-status">
              {wsConnected ? <Wifi className="w-3 h-3 text-success" /> : <WifiOff className="w-3 h-3 text-destructive" />}
            </div>

            {/* Alert Bell */}
            <div className="relative">
              <button onClick={() => setBellOpen(!bellOpen)} className="relative p-1.5 text-muted-foreground hover:text-foreground transition-colors" data-testid="alert-bell-btn">
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-destructive text-[9px] font-bold text-white flex items-center justify-center" data-testid="unread-badge">
                    {unreadCount > 9 ? "9+" : unreadCount}
                  </span>
                )}
              </button>
              {bellOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setBellOpen(false)} />
                  <div className="absolute right-0 top-full mt-2 w-80 bg-card border border-border shadow-xl z-50" data-testid="alert-dropdown">
                    <div className="flex items-center justify-between p-3 border-b border-border">
                      <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Recent Alerts</span>
                      <button onClick={() => { setBellOpen(false); navigate("/alerts"); }} className="text-xs text-primary hover:underline" data-testid="view-all-alerts-link">View All</button>
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {alerts.slice(0, 8).length === 0 ? (
                        <div className="text-center text-muted-foreground text-xs py-6">No alerts yet</div>
                      ) : (
                        alerts.slice(0, 8).map((a) => (
                          <div key={a.id} className={`px-3 py-2 border-b border-border/50 text-xs ${a.read ? "opacity-50" : ""}`}>
                            <div className="flex items-center gap-1.5 mb-0.5">
                              <span className={`w-1.5 h-1.5 flex-shrink-0 ${a.severity === "CRITICAL" ? "bg-destructive" : a.severity === "WARNING" ? "bg-warning" : "bg-primary"}`} />
                              <span className="font-semibold truncate">{a.title}</span>
                            </div>
                            <p className="text-muted-foreground truncate pl-3">{a.message}</p>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="flex-1 p-4 lg:p-6 overflow-auto">{children}</div>
      </main>
    </div>
  );
};

/* ============ Price Pill for ticker bar ============ */
const MiniSparkline = ({ data, width = 40, height = 14 }) => {
  if (!data || data.length < 2) return null;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;
  const isUp = data[data.length - 1] >= data[0];
  const color = isUp ? "#00ff88" : "#ff4444";
  
  const points = data.map((v, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((v - min) / range) * (height - 2) - 1;
    return `${x},${y}`;
  }).join(" ");

  return (
    <svg width={width} height={height} className="flex-shrink-0">
      <polyline points={points} fill="none" stroke={color} strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

const PricePill = ({ label, value, decimals = 2, sparkline }) => {
  const [prev, setPrev] = useState(value);
  const [dir, setDir] = useState("");

  useEffect(() => {
    if (value && prev && value !== prev) {
      setDir(value > prev ? "up" : "down");
      const t = setTimeout(() => setDir(""), 800);
      setPrev(value);
      return () => clearTimeout(t);
    }
    if (value) setPrev(value);
  }, [value, prev]);

  if (!value) return null;

  return (
    <div className={`flex items-center gap-1.5 transition-colors duration-300 ${dir === "up" ? "text-success" : dir === "down" ? "text-destructive" : "text-foreground"}`} data-testid={`price-${label.toLowerCase()}`}>
      <span className="text-muted-foreground text-[10px]">{label}</span>
      <MiniSparkline data={sparkline} />
      <span className="font-semibold">
        ${value.toLocaleString(undefined, { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}
      </span>
    </div>
  );
};

export default DashboardLayout;
